/** Welcher Reiter die Wahrheit zeigt, wenn die Glocke aufgeht. */
import { vorschlagIdentitaet } from "../../shared/vorschlag-identitaet.js";

type OffenTab = "tasks" | "notes" | "emails";

/*
 * ⚠️ 2026-09-03 (Martin, mit Bild: „Meldungen soll Default sein, nicht
 * Aufgaben."):
 *
 * Hier stand `tasks` an erster Stelle — dieselbe Sicht, die er am selben Tag
 * schon fuer die REIHENFOLGE der Reiter korrigiert hat („An erster Position
 * sind die Aufgaben, die in Wirklichkeit mit mir nichts zu tun haben").
 * Die Reihenfolge wurde umgestellt, dieser Standard trug den alten Gedanken
 * weiter — deshalb sprang der Reiter beim Oeffnen zurueck auf „Aufgaben",
 * obwohl „Meldungen" vorne steht.
 *
 * Die Rangfolge folgt jetzt der Zugehoerigkeit, nicht der Dringlichkeit:
 * Meldungen sind SEINE Sachen (Klimmzuege, Raus mit dir, die tiefe Frage),
 * E-Mails schreiben ihm Menschen, „Aufgaben" legt Vespera fuer sich selbst
 * an. Was ihn betrifft, kommt zuerst.
 *
 * ⚠️ Auch der Rueckfall bei LEERER Glocke ist jetzt „notes" statt „tasks":
 * ein leerer Meldungs-Reiter ist eine gute Nachricht, ein Entwickler-Ticket
 * als Startbild ist keine.
 */
export function waehleOffenTab(counts: {
  tasks: number;
  notes: number;
  emails: number;
}): OffenTab {
  if (counts.notes > 0) return "notes";
  if (counts.emails > 0) return "emails";
  if (counts.tasks > 0) return "tasks";
  return "notes";
}

/**
 * Der Vorspann, den keine Anzeige braucht: „Erinnerung:", „Geplant:".
 *
 * ⚠️ Er ist ein MARKER in der Datenbank, kein Wort fuer Martin — daran
 *    erkennt das Haus die wiederkehrende Aufgabe ({@link istErinnerungTitel}),
 *    und jede der Bestandszeilen traegt ihn. Darum wird die ANZEIGE
 *    umbenannt, nicht der Bestand.
 */
const VORSPANN = /^\s*[\u23f0\u2709\u{1F4E7}]?\s*(Erinnerung|Geplant):\s*/iu;

/**
 * Der Titel, wie er auf der Seite steht.
 *
 * Martin, 08.09.2026: *„Erinnerungen sollten eigentlich statt ‚Erinnerungen'
 * ‚Aufgaben' heißen."* Die Zeile heisst also nicht mehr „Erinnerung: 5 bis 10
 * Klimmzüge", sondern „5 bis 10 Klimmzüge" — das Wort stand nur davor und
 * sagte nichts, was die Liste nicht schon sagt.
 *
 * ⚠️ Bleibt nach dem Abschneiden nichts uebrig, gilt der ganze Titel. Eine
 *    leere Zeile waere schlimmer als ein Vorspann.
 */
export function titelFuerAnzeige(titel: string): string {
  return titel.replace(VORSPANN, "").replace(/\s{2,}/g, " ").trim() || titel.trim();
}

/** Titel vergleichbar: ohne Erinnerung-Prefix, ohne Post-Emoji, klein.
 *  Derselbe wiederkehrende Fehler mit verschiedenem Labor-Suffix ist einer. */
export function offenSchluessel(titel: string): string {
  const vor = titel
    .replace(VORSPANN, "")
    .replace(/[\u2709\u{1F4E7}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  return vorschlagIdentitaet(vor);
}

/** Dieselbe offene Sache nur einmal. Die erste Zeile bleibt. */
export function einzigeOffene<T extends { title?: string; titel?: string }>(
  rows: T[],
): T[] {
  const gesehen = new Set<string>();
  const aus: T[] = [];
  for (const r of rows) {
    const k = offenSchluessel(String(r.title || r.titel || ""));
    if (!k || gesehen.has(k)) continue;
    gesehen.add(k);
    aus.push(r);
  }
  return aus;
}

/** Alle offenen IDs derselben Sache — sichtbar plus versteckte Zwillinge. */
export function zwillingsIds(
  id: number,
  alle: Array<{ id: number; title?: string; titel?: string }>,
): number[] {
  const anker = alle.find((t) => t.id === id);
  if (!anker) return [id];
  const key = offenSchluessel(String(anker.title || anker.titel || ""));
  if (!key) return [id];
  const ids = alle
    .filter((t) => offenSchluessel(String(t.title || t.titel || "")) === key)
    .map((t) => t.id);
  return ids.length > 0 ? ids : [id];
}

/** Eine Zeile aus der Offen-Fläche — Badge, Zuhause und Reiter lesen dieselbe. */
type OffenZeile = {
  title?: string;
  titel?: string;
  description?: string;
  kind?: string | null;
};

/** Mail-Reiter: dieselbe Regel wie das Mail-Icon. */
export function istOffenMail(eintrag: OffenZeile): boolean {
  const kind = (eintrag.kind || "ticket").toLowerCase();
  if (kind !== "notification") return false;
  const s = `${eintrag.title || eintrag.titel || ""} ${eintrag.description || ""}`.toLowerCase();
  return /mail|e-mail|posteingang|gmail/.test(s);
}

/** Offener Faden (Ticket 276) — wartet auf Antwort/Entscheidung/Zusage. */
export function istOffenFaden(eintrag: OffenZeile): boolean {
  return (eintrag.kind || "").toLowerCase() === "thread";
}

/**
 * Was ein Klick auf eine offene Zeile auslöst. (07.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Wenn ich jetzt draufklicke, ‚Das Großvater-Muster in Hollenthon
 * beleuchten', sollte dann nicht Vespera ‚Push to Talk' aufgehen, damit ich
 * mit ihm darüber spreche? Stattdessen geht es auf ‚Was offen ist',
 * ‚Meldungen' und 13 Aufgaben. Das ist ja nicht Sinn und Zweck der Sache."*
 *
 * Ein Faden ist eine Einladung zum Reden. Eine tiefe Frage will beantwortet
 * werden. Alles andere führt an seinen Ort. Diese Regel stand als
 * verschachteltes `if` mitten in einer Oberflächen-Funktion und war damit
 * nur über den Quelltext prüfbar — hier ist sie eine Zeile Logik, die man
 * mit echten Zeilen befragen kann.
 */
type ZeilenHandlung = "gespraech" | "frage" | "ziel";

/**
 * ⚠️ Der Titel bleibt nur der RÜCKFALL für die Frage. Seit dem 05.09. trägt
 *    die Zeile `art: "frage"`; Zeilen aus der alten Zeit haben das nicht.
 */
export function handlungFuerZeile(zeile: {
  faden?: boolean;
  art?: string | null;
  titel?: string | null;
}): ZeilenHandlung {
  if (zeile.faden) return "gespraech";
  const titel = zeile.titel ?? "";
  if (zeile.art === "frage" || /tiefe frage|frage des tages|tiefen-faden/i.test(titel)) {
    return "frage";
  }
  return "ziel";
}

/** Wiederkehrende Erinnerung — gemacht oder nicht gemacht, nicht nur abhaken. */
export function istErinnerungTitel(titel: string | null | undefined): boolean {
  return /^\s*Erinnerung:\s+/i.test(titel ?? "");
}

export function offenArt(eintrag: OffenZeile): OffenTab {
  const kind = (eintrag.kind || "ticket").toLowerCase();
  if (kind !== "notification") return "tasks";
  return istOffenMail(eintrag) ? "emails" : "notes";
}

/** Fäden zuerst — wartende Punkte nicht unter Vorschlägen begraben. */
export function sortiereFaedenZuerst<T extends OffenZeile>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const fa = istOffenFaden(a) ? 0 : 1;
    const fb = istOffenFaden(b) ? 0 : 1;
    return fa - fb;
  });
}

type OffeneTeilung<T extends OffenZeile> = {
  tasks: T[];
  notes: T[];
  emails: T[];
  liste: T[];
  zahl: number;
};

/**
 * Eine Zählung für Dock, Zuhause und Reiter.
 * Dock öffnet die ganze Offen-Fläche — Zahl ist die Summe der Reiter,
 * jede Sache in ihrem Reiter nur einmal.
 */
export function teileOffene<T extends OffenZeile>(rows: T[]): OffeneTeilung<T> {
  const tasks = einzigeOffene(rows.filter((r) => offenArt(r) === "tasks"));
  const notes = einzigeOffene(rows.filter((r) => offenArt(r) === "notes"));
  const emails = einzigeOffene(rows.filter((r) => offenArt(r) === "emails"));
  const ja = new Set<T>([...tasks, ...notes, ...emails]);
  const liste = sortiereFaedenZuerst(rows.filter((r) => ja.has(r)));
  return {
    tasks: sortiereFaedenZuerst(tasks),
    notes,
    emails,
    liste,
    zahl: tasks.length + notes.length + emails.length,
  };
}
